
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import '../styles.css';

const UpdateRoleOffice = () => {
  const { userId } = useParams();
  const { roleId } = useParams();
  const { unitId } = useParams();
  const { officeId } = useParams();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState({});
  const [currentUserId] = useState(localStorage.getItem('id'));
  const [roleData, setRoleData] = useState({ name: '' });
  const [locationData, setLocationData] = useState({
    name: '',
    latitude: '',
    longitude: '',
    unit: '',
  });
  const [unitData, setUnitData] = useState({ name: '' });
  const [units, setUnits] = useState([]);
  const [updateType, setUpdateType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [roleError, setRoleError] = useState('');
  const [rolesData, setRolesData] = useState([]);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);

  useEffect(() => {
    const screenWidth = window.innerWidth;
    setIsSidebarVisible(screenWidth >= 520);
  }, []);

  const handleToggleClick = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    axiosInstance.get(`/user/${currentUserId}/`)
      .then(response => {
        const userData = response.data;
        setCurrentUser({ ...userData, role: userData.role?.name || 'guest' });
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    axiosInstance.get(`/users/${currentUserId}/roles/`)
      .then(res => {
        setRolesData([res.data.active_role, res.data.assigned_role]);
      })
      .catch(console.error);
  }, []);

  const handleSwitchRole = async () => {
    try {
      await axiosInstance.post('/switch-role/', { role_id: rolesData[1].id });
      navigate('/user-dashboard');
    } catch (err) {
      setRoleError(err.response?.data?.detail || "Failed to switch role");
    }
  };

  useEffect(() => {
    axiosInstance.get('/branch-unit/')
      .then(response => {
        setUnits(response.data);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (roleId) {
      axiosInstance.get(`/role/${roleId}/`)
        .then(response => {
          const roleData = response.data;
          setRoleData({ name: roleData.name });
          setUpdateType('role');
        })
        .catch(console.error);
    }
  }, [roleId]);

  useEffect(() => {
    if (officeId) {
      axiosInstance.get(`/office-branch/${officeId}/`)
        .then(response => {
          const locationData = response.data;
          setLocationData({ 
            name: locationData.name, 
            latitude: locationData.latitude, 
            longitude: locationData.longitude,
            unit: locationData.unit 
          });
          setUpdateType('location');
        })
        .catch(console.error);
    }
  }, [officeId]);

  useEffect(() => {
    if (unitId) {
      axiosInstance.get(`/branch-unit/${unitId}/`)
        .then(response => {
          const unitData = response.data;
          setUnitData({ name: unitData.name });
          setUpdateType('unit');
        })
        .catch(console.error);
    }
  }, [unitId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRoleData({ ...roleData, [name]: value });
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const handleOfficeChange = (e) => {
    const { name, value } = e.target;
    setLocationData({ ...locationData, [name]: value });
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const handleUnitChange = (e) => {
    const { name, value } = e.target;
    setUnitData({ ...unitData, [name]: value });
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    axiosInstance.patch(`/role/${roleId}/`, roleData)
      .then(() => {
        setSuccess('Role updated successfully!');
        setTimeout(() => navigate('/roles-office'), 1500);
      })
      .catch(err => {
        console.error(err);
        setError('Failed to update role. Please try again.');
      })
      .finally(() => setIsLoading(false));
  };

  const handleOfficeSubmit = (e) => {
    e.preventDefault();
    if (!locationData.unit) {
      setError('Please select a unit for the office branch.');
      return;
    }
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    axiosInstance.patch(`/office-branch/${officeId}/`, locationData)
      .then(() => {
        setSuccess('Location updated successfully!');
        setTimeout(() => navigate('/roles-office'), 1500);
      })
      .catch(err => {
        console.error(err);
        setError('Failed to update location. Please try again.');
      })
      .finally(() => setIsLoading(false));
  };

  const handleUnitSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    axiosInstance.patch(`/branch-unit/${unitId}/`, unitData)
      .then(() => {
        setSuccess('Unit updated successfully!');
        setTimeout(() => navigate('/roles-office'), 1500);
      })
      .catch(err => {
        console.error(err);
        setError('Failed to update unit. Please try again.');
      })
      .finally(() => setIsLoading(false));
  };

  const handleLogout = () => {
    axiosInstance.post('/logout/', { refresh: localStorage.getItem('refresh') })
      .then(() => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('employee_code');
        localStorage.removeItem('assignedRole');
        localStorage.removeItem('id'); 
        window.location.href = '/';
      })
      .catch(console.error);
  };

  const getPageTitle = () => {
    if (updateType === 'role') return 'Update Role Information';
    if (updateType === 'location') return 'Update Office Branch Information';
    return 'Update Unit Information';
  };

  const getIcon = () => {
    if (updateType === 'role') return '&#128100;';
    if (updateType === 'location') return '&#127968;';
    return '&#128193;';
  };

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header onToggleClick={handleToggleClick} isSidebarVisible={isSidebarVisible} />
      <Sidebar 
        isSidebarVisible={isSidebarVisible} 
        currentUser={currentUser} 
        activeRole={currentUser?.role}
        error={roleError}
        handleSwitchRole={handleSwitchRole}
        handleLogout={handleLogout} 
      />
      
      <main className="dashboard-main-content">
        <div className="dashboard-header-section">
          <h1>{getPageTitle()}</h1>
          <p>Edit and update the details</p>
        </div>

        <div className="update-role-card">
          {error && (
            <div className="form-error-message">
              <span>!</span> {error}
            </div>
          )}
          
          {success && (
            <div className="form-success-message">
              <span>&#10003;</span> {success}
            </div>
          )}

          {updateType === 'role' ? (
            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <div className="section-header">
                  <span className="section-icon" dangerouslySetInnerHTML={{ __html: getIcon() }}></span>
                  <h3>Role Information</h3>
                </div>
                
                <div className="update-form-grid">
                  <div className="form-group">
                    <label htmlFor="name">Role Name *</label>
                    <input 
                      type="text" 
                      name="name" 
                      value={roleData.name} 
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={isLoading}>
                  {isLoading ? <span className="loading-spinner"></span> : <><span>+</span> Update Role</>}
                </button>
                <button type="button" className="cancel-btn" onClick={() => navigate('/roles-office')}>
                  Cancel
                </button>
              </div>
            </form>
          ) : updateType === 'location' ? (
            <form onSubmit={handleOfficeSubmit}>
              <div className="form-section">
                <div className="section-header">
                  <span className="section-icon" dangerouslySetInnerHTML={{ __html: getIcon() }}></span>
                  <h3>Office Branch Information</h3>
                </div>
                
                <div className="update-form-grid">
                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input 
                      type="text" 
                      name="name" 
                      value={locationData.name} 
                      onChange={handleOfficeChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="latitude">Latitude</label>
                    <input 
                      type="text" 
                      name="latitude" 
                      value={locationData.latitude} 
                      onChange={handleOfficeChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="longitude">Longitude</label>
                    <input 
                      type="text" 
                      name="longitude" 
                      value={locationData.longitude} 
                      onChange={handleOfficeChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="unit">Unit *</label>
                    <select 
                      name="unit" 
                      value={locationData.unit} 
                      onChange={handleOfficeChange}
                      required
                    >
                      <option value="">Select Unit</option>
                      {units.map(unit => (
                        <option key={unit.id} value={unit.id}>{unit.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={isLoading}>
                  {isLoading ? <span className="loading-spinner"></span> : <><span>+</span> Update Location</>}
                </button>
                <button type="button" className="cancel-btn" onClick={() => navigate('/roles-office')}>
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleUnitSubmit}>
              <div className="form-section">
                <div className="section-header">
                  <span className="section-icon" dangerouslySetInnerHTML={{ __html: getIcon() }}></span>
                  <h3>Unit Information</h3>
                </div>
                
                <div className="update-form-grid">
                  <div className="form-group">
                    <label htmlFor="name">Unit Name *</label>
                    <input 
                      type="text" 
                      name="name" 
                      value={unitData.name} 
                      onChange={handleUnitChange}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={isLoading}>
                  {isLoading ? <span className="loading-spinner"></span> : <><span>+</span> Update Unit</>}
                </button>
                <button type="button" className="cancel-btn" onClick={() => navigate('/roles-office')}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default UpdateRoleOffice;


